import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useVideosActions } from 'src/store/videos/actions';
import { QuestionArrayModel, ScrollQuestionModel } from 'src/models/videosModel';
import Loader from 'src/components/loader';

interface VideoItem {
    id: string;
    name: string;
    _chapterName: string;
}

interface CourseQuizBuilderModalProps {
    isOpen: boolean;
    courseSequences: any;
    singleCourse: any;
    onClose: () => void;
    onQuizSaved?: () => void;
}


const CourseQuizBuilderModal: React.FC<CourseQuizBuilderModalProps> = ({
    isOpen,
    courseSequences,
    singleCourse,
    onClose,
    onQuizSaved
}) => {
    const dispatch = useDispatch();
    const videoActions = useVideosActions(dispatch);
    const [deletingPollId, setDeletingPollId] = useState<string | null>(null);
    type Step = 'video-select' | 'quiz-builder' | 'success';
    const [step, setStep] = useState<Step>('video-select');
    const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

    const [editingPollId, setEditingPollId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);
    const [addQuestionOpen, setAddQuestionOpen] = useState(false);
    const [questionArray, setQuestionArray] = useState<Array<QuestionArrayModel>>([
        { type: 'qt1', qId: 1, option: [], description: false }
    ]);
    const [questionId, setQuestionId] = useState(2);
    const [scrollQuestion, setScrollQuestion] = useState<ScrollQuestionModel[]>([]);
    const [displayResultData, setDisplayResultData] = useState(false);
    const [answerSelectError, setAnswerSelectError] = useState(false);
    const [isDataSubmitted, setIsDataSubmitted] = useState(false);
    const [totalPoints, setTotalPoints] = useState<number>(1);
    const [time, setTime] = useState<string>('00:01:00');


    const { isPollSaved, isPollSavedLoading, polls, pollsResult, isPollDeleted } = useSelector((state: RootState) => ({
        isPollSaved: state.videos.isPollSaved,
        isPollSavedLoading: state.videos.isPollSavedLoading,
        polls: state.videos.polls,
        pollsResult: state.videos.pollsResult,
        isPollDeleted: state.videos.isPollDeleted,
    }));


    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        unregister
    } = useForm();

    const watchAllFields = watch();


    const allVideos: VideoItem[] = (courseSequences?.contents ?? [])
        .flatMap((chapter: any) =>
            (chapter?.contentOrders ?? [])
                .filter((c: any) => c?.contentTypes === 1)
                .map((c: any) => ({ ...c, _chapterName: chapter?.chapterName }))
        )
        .filter(Boolean);


    useEffect(() => {
        const sub = watch((value, { name }) => {
            if (name?.includes('question')) {
                setScrollQuestion(
                    questionArray.map((item) => ({
                        name: watch(`question_${item.qId}`),
                        id: item.qId
                    }))
                );
            }
            if (name?.includes('points') || name?.includes('question')) {
                const pts = questionArray
                    .map((item) => watch(`points_${item.qId}`))
                    .filter((p) => p !== '')
                    .reduce((a: any, b: any) => parseInt(a) + parseInt(b), 0);
                if (pts) setTotalPoints(pts);
            }
        });
        return () => sub.unsubscribe();
    }, [watchAllFields, questionArray]);


    useEffect(() => {
        if (totalPoints === 0) { setAnswerSelectError(false); return; }
        const qt1 = questionArray.filter((q) => q.type === 'qt1');
        setAnswerSelectError(qt1.some((q) => !q.option.some((o) => o.isAnswer)));
    }, [questionArray, totalPoints]);

    useEffect(() => {
        if (isPollDeleted && expandedVideoId) {
            videoActions.getPollRequest(expandedVideoId);
            videoActions.resetVideoLibraryRequest()
            setDeletingPollId(null);
        }
    }, [isPollDeleted]);

    useEffect(() => {
        if (isPollSaved) {
            setStep('success');
            onQuizSaved?.();
            if (selectedVideo?.id) {
                videoActions.getPollRequest(selectedVideo.id);
            }
            videoActions.resetVideoLibraryRequest()
        }
    }, [isPollSaved]);


    useEffect(() => {
        if (pollsResult && isEditMode) {
            const scrollQuestionReset: Array<ScrollQuestionModel> = [];
            const questionArr: any[] = [];
            const resetAnswerData: any = {};
            const totalPts: number[] = [];

            pollsResult?.quizzes?.questions?.forEach((item: any, index: number) => {
                if (!item.quizOpenTextQuestion) {
                    const choiceData: any[] = [];
                    item.choices?.forEach((choice: any, idx: number) => {
                        choiceData.push({ option: `option_${index + 1}`, isAnswer: choice.isAnswer });
                        resetAnswerData[`option_${index + 1}_${idx}`] = choice.answerText;
                    });
                    questionArr.push({
                        type: 'qt1',
                        qId: index + 1,
                        option: choiceData,
                        description: item.description !== ''
                    });
                    resetAnswerData[`question_${index + 1}`] = item.questionText;
                    resetAnswerData[`points_${index + 1}`] = item.points;
                    resetAnswerData[`description_${index + 1}`] = item.description;
                } else {
                    questionArr.push({
                        type: 'qt2',
                        qId: index + 1,
                        option: [],
                        description: item.description !== '',
                        allowResourceUpload: item.allowResourceUpload,
                        isTextAnswer: item.isTextAnswer
                    });
                    resetAnswerData[`question_${index + 1}`] = item.questionText;
                    resetAnswerData[`points_${index + 1}`] = item.points;
                    resetAnswerData[`description_${index + 1}`] = item.description;
                    resetAnswerData[`text_answer_${index + 1}`] = item.textAnswer;
                }
                scrollQuestionReset.push({ name: item.questionText, id: index + 1 });
                totalPts.push(item.points);
            });

            setQuestionArray(questionArr);
            setScrollQuestion(scrollQuestionReset);
            setQuestionId(questionArr.length + 1);
            setTotalPoints(totalPts.reduce((a, b) => a + b, 0));
            setDisplayResultData(pollsResult.displayResults);
            setTime(pollsResult.pollDisplayTime ?? '00:01:00');
            reset({
                quizname: pollsResult.pollTitle,
                displayResultsTimer: pollsResult.displayResultsTimer,
                ...resetAnswerData
            });
            setStep('quiz-builder');
        }
    }, [pollsResult, isEditMode]);


    const resetBuilder = () => {
        setTime('00:01:00');
        setQuestionArray([{ type: 'qt1', qId: 1, option: [], description: false }]);
        setScrollQuestion([]);
        setDisplayResultData(false);
        setTotalPoints(1);
        setIsDataSubmitted(false);
        setQuestionId(2);
        setEditingPollId(null);
        setIsEditMode(false);
        reset({ question_1: '', pollType: 0, quizname: '', points_1: 1 });
    };


    const handleToggleVideoExpand = (video: VideoItem) => {
        if (expandedVideoId === video.id) {
            setExpandedVideoId(null);
        } else {
            setExpandedVideoId(video.id);
            // Load polls for this video (pollType 0 = quiz)
            videoActions.getPollRequest(video.id);
        }
    };


    const handleAddNewQuiz = (video: VideoItem) => {
        setSelectedVideo(video);
        setIsEditMode(false);
        setEditingPollId(null);
        resetBuilder();
        setStep('quiz-builder');
    };


    const handleEditQuiz = (video: VideoItem, poll: any) => {
        setSelectedVideo(video);
        setIsEditMode(true);
        setEditingPollId(poll.pollId);
        videoActions.getPollsResultRequest(poll.pollId);
    };

    const handleAddOption = (qId: number) => {
        setQuestionArray((prev) =>
            prev.map((item) => {
                if (item.qId === qId && item.type === 'qt1') {
                    return {
                        ...item,
                        option: [...item.option, { option: `option_${qId}_${item.option.length}`, isAnswer: false }]
                    };
                }
                return item;
            })
        );
    };

    const handleCloseQuestion = (id: number) => {
        unregister([`question_${id}`, `points_${id}`, `description_${id}`]);
        setQuestionArray((prev) => prev.filter((item) => item.qId !== id));
        setScrollQuestion((prev) => prev.filter((q) => q.id !== id));
    };

    const handleAddQuestion = () => {
        setIsDataSubmitted(false);
        const newId = questionId;
        setQuestionId((x) => x + 1);
        setQuestionArray((prev) => [...prev, { type: 'qt1', qId: newId, option: [], description: false }]);
    };

    const handleAddOpenText = () => {
        setIsDataSubmitted(false);
        const newId = questionId;
        setQuestionId((x) => x + 1);
        setQuestionArray((prev) => [...prev, { type: 'qt2', qId: newId, option: [], description: false, isTextAnswer: false }]);
    };

    const handleDescriptionClick = (qId: number) => {
        setQuestionArray((prev) => prev.map((item) => (item.qId === qId ? { ...item, description: true } : item)));
    };

    const handleIsAnswerClick = (qId: number, optionId: number) => {
        setQuestionArray((prev) =>
            prev.map((item) => {
                if (item.qId === qId) {
                    return { ...item, option: item.option.map((opt, idx) => ({ ...opt, isAnswer: idx === optionId })) };
                }
                return item;
            })
        );
    };

    const handleIsAnswerTextClick = (qId: number) => {
        setQuestionArray((prev) =>
            prev.map((item) =>
                item.qId === qId && item.type === 'qt2' ? { ...item, isTextAnswer: !item.isTextAnswer } : item
            )
        );
    };

    const onSubmit = (data: any) => {
        if (!selectedVideo) return;
        setIsDataSubmitted(true);
        if (answerSelectError) return;


        const question: any[] = [];
        for (const qItem of questionArray) {
            const i = qItem.qId;
            if (data[`question_${i}`]) {
                const choiceArray: any[] = [];
                if (qItem.type === 'qt1') {
                    qItem.option.forEach((opt, j) => {
                        const val = data[`${opt.option}_${j}`] ?? data[opt.option];
                        if (val) {
                            choiceArray.push({
                                position: j + 1,
                                answerText: val,
                                isNewOption: true,
                                isAnswer: opt.isAnswer
                            });
                        }
                    });
                }
                question.push({
                    position: i,
                    description: data[`description_${i}`] ?? '',
                    questionText: data[`question_${i}`],
                    points: data[`points_${i}`] ? data[`points_${i}`] : 1,
                    allowResourceUpload: data[`allowResourceUpload_${i}`] ?? false,
                    isNewQuestion: true,
                    quizOpenTextQuestion: qItem.type === 'qt2',
                    choices: choiceArray,
                    isTextAnswer: data[`isTextAnswer_${i}`] ?? false,
                    textAnswer: data[`text_answer_${i}`]
                });
            }
        }

        if (isEditMode && editingPollId && pollsResult) {

            const updatePayload = {
                pollId: editingPollId,
                videoId: selectedVideo.id,
                activePollStatus: pollsResult?.activePollStatus,
                pollTitle: data.quizname,
                pollType: 0,
                pollDisplayTimeString: time,
                displayResults: displayResultData,
                displayResultsTimer: data.displayResultsTimer,
                quizzes: {
                    id: pollsResult.quizzes.id,
                    name: data.quizname,
                    questions: question
                }
            };
            videoActions.updatePollRequest(updatePayload);
        } else {

            const createPayload = {
                videoId: selectedVideo.id,
                pollTitle: data.quizname,
                pollType: 0,
                pollDisplayTimeString: time,
                displayResults: displayResultData,
                displayResultsTimer: data.displayResultsTimer,
                quizzes: {
                    name: data.quizname,
                    questions: question
                }
            };
            videoActions.savePollRequest(createPayload);
        }
    };

    const handleAddAnother = () => {
        setSelectedVideo(null);
        setStep('video-select');
        resetBuilder();
    };

    const handleClose = () => {
        resetBuilder();
        setSelectedVideo(null);
        setStep('video-select');
        setExpandedVideoId(null);
        onClose();
    };


    const getQuizzesForVideo = (videoId: string) => {
        const typedPolls = polls as Record<string, any[]> | any[];

        if (!typedPolls) return [];

        if (Array.isArray(typedPolls)) {
            return typedPolls.filter(
                (p) => p.videoId === videoId && p.pollType === 0
            );
        }

        return (typedPolls[videoId] ?? []).filter(
            (p) => p.pollType === 0
        );
    };

    if (!isOpen) return null;

    return (
        <div
            style={{ zIndex: 100000 }}
            className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 py-6 px-4">
            <div
                className="relative w-full pointer-events-auto bg-white rounded-xl shadow-lg"
                style={{ maxWidth: step === 'quiz-builder' ? '860px' : '560px' }}>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        {step === 'quiz-builder' && (
                            <button
                                type="button"
                                onClick={() => { setStep('video-select'); setIsEditMode(false); setEditingPollId(null); }}
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
                                <i className="fa-solid fa-arrow-left text-xs" />
                                Back
                            </button>
                        )}
                        <h5 className="text-base font-semibold text-gray-800">
                            {step === 'video-select' && 'Course Quizzes'}
                            {step === 'quiz-builder' && (isEditMode ? 'Edit Quiz' : 'Create Quiz')}
                            {step === 'success' && (isEditMode ? 'Quiz Updated' : 'Quiz Saved')}
                        </h5>
                    </div>
                    <div className="flex items-center gap-3">
                        {step === 'quiz-builder' && (
                            <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-3 py-1 rounded-full">
                                {isEditMode ? 'Edit mode' : 'Step 2 of 2'}
                            </span>
                        )}

                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors text-xl leading-none">
                            ×
                        </button>
                    </div>
                </div>

                {/* Step 1: Video list with quiz management  */}
                {step === 'video-select' && (
                    <>
                        <div className="p-5">
                            <p className="text-sm text-gray-500 mb-4">
                                Add a new quiz to a video or edit an existing one.
                            </p>
                            {allVideos.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-10">
                                    No videos found in this course.
                                </p>
                            ) : (
                                <div className="rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100 max-h-96 overflow-y-auto">
                                    {allVideos.map((video) => {
                                        const isExpanded = expandedVideoId === video.id;
                                        const quizzes = getQuizzesForVideo(video.id);

                                        return (
                                            <div key={video.id}>
                                                {/* Video row */}
                                                <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                                                    <i className="fa-solid fa-circle-play text-gray-300 text-lg flex-shrink-0" />
                                                    <div className="flex flex-col min-w-0 flex-1">
                                                        <span className="text-sm font-medium text-gray-700 truncate">
                                                            {video.name}
                                                        </span>
                                                        <span className="text-xs text-gray-400 truncate">
                                                            {video._chapterName}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAddNewQuiz(video)}
                                                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                                                            <i className="fa-solid fa-plus text-xs" />
                                                            Add Quiz
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleVideoExpand(video)}
                                                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors">
                                                            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-xs`} />
                                                            Quizzes
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Expanded: show existing quizzes for this video */}
                                                {isExpanded && (
                                                    <div className="bg-gray-50 border-t border-gray-100 px-4 py-2">
                                                        {quizzes.length === 0 ? (
                                                            <p className="text-xs text-gray-400 py-3 text-center">
                                                                No quizzes yet for this video.
                                                            </p>
                                                        ) : (
                                                            <div className="space-y-1.5 py-1">
                                                                {quizzes.map((quiz: any) => (
                                                                    <div
                                                                        key={quiz.pollId}
                                                                        className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200">
                                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                            <i className="fa-solid fa-circle-question text-indigo-400 text-sm flex-shrink-0" />
                                                                            <span className="text-sm font-medium text-gray-700 truncate">
                                                                                {quiz.pollTitle}
                                                                            </span>
                                                                            {quiz.pollDisplayTime && (
                                                                                <span className="text-xs text-gray-400 flex-shrink-0 bg-gray-100 px-2 py-0.5 rounded">
                                                                                    {quiz.pollDisplayTime}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleEditQuiz(video, quiz)}
                                                                            className="flex items-center gap-1 text-xs px-3 py-1 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors flex-shrink-0 ml-3">
                                                                            <i className="fa-solid fa-pen text-xs" />
                                                                            Edit
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            disabled={deletingPollId === quiz.pollId}
                                                                            onClick={() => {

                                                                                setDeletingPollId(quiz.pollId);
                                                                                videoActions.deletePollRequest(quiz.pollId);

                                                                            }}
                                                                            className={`ml-1 flex items-center gap-1 text-xs px-3 py-1 rounded-lg border transition-colors ${deletingPollId === quiz.pollId
                                                                                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                                                                                : 'border-red-200 text-red-500 hover:bg-red-50'
                                                                                }`}>
                                                                            {deletingPollId === quiz.pollId ? (
                                                                                <i className="fa-solid fa-spinner fa-spin text-xs" />
                                                                            ) : (
                                                                                <i className="fa-solid fa-trash text-xs" />
                                                                            )}
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors">
                                Close
                            </button>
                        </div>
                    </>
                )}

                {/* Step 2: Quiz Builder */}
                {step === 'quiz-builder' && (
                    <div>
                        {/* Context bar */}
                        <div className="flex items-center gap-2 px-5 py-2 bg-indigo-50 border-b border-indigo-100">
                            <i className="fa-solid fa-circle-play text-indigo-400 text-sm" />
                            <span className="text-xs text-indigo-600">
                                {isEditMode ? 'Editing quiz for: ' : 'Attaching quiz to: '}
                                <strong className="font-semibold">{selectedVideo?.name}</strong>
                                <span className="text-indigo-400 ml-1">({selectedVideo?._chapterName})</span>
                            </span>
                        </div>

                        <div className="flex" style={{ minHeight: '440px', maxHeight: '60vh' }}>

                            {/* Left sidebar */}
                            <div className="flex flex-col border-r border-gray-100 bg-gray-50" style={{ width: '220px', flexShrink: 0 }}>
                                <div className="p-4 border-b border-gray-100 space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Quiz name *</label>
                                        <input
                                            type="text"
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400"
                                            placeholder="e.g. Chapter 1 Quiz"
                                            {...register('quizname', { required: true })}
                                        />
                                        {errors.quizname && (
                                            <p className="text-red-500 text-xs mt-1">Quiz name is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            Display time (hh:mm:ss)
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400"
                                            value={time}
                                            onChange={(e) => setTime(e.target.value)}
                                            placeholder="00:01:00"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-medium text-gray-500">Show results</label>
                                        <div className="form-check form-switch pl-0 relative">
                                            <input
                                                onChange={(e) => setDisplayResultData(e.target.checked)}
                                                className="form-check-input switch-btn"
                                                type="checkbox"
                                                role="switch"
                                                checked={displayResultData}
                                            />
                                            <i className="fas fa-check checkbox-tick hidden" />
                                        </div>
                                    </div>
                                    {displayResultData && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                Result timer (s)
                                            </label>
                                            <select
                                                className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
                                                {...register('displayResultsTimer')}>
                                                {[15, 30, 45, 60, 90, 120].map((v) => (
                                                    <option key={v} value={v}>{v}s</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Question nav */}
                                <div className="flex-1 overflow-y-auto py-2">
                                    <p className="text-xs font-medium text-gray-400 px-4 mb-1 uppercase tracking-wide">Questions</p>
                                    {scrollQuestion.map((q, idx) => (
                                        <a
                                            key={q.id}
                                            href={`#cq-Question${q.id}`}
                                            className="flex items-center gap-2 px-4 py-2 text-xs text-gray-600 hover:bg-white hover:text-indigo-600 transition-colors rounded">
                                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                                {idx + 1}
                                            </span>
                                            <span className="truncate">{q.name || 'Untitled question'}</span>
                                        </a>
                                    ))}
                                </div>

                                <div className="p-4 border-t border-gray-100 text-center">
                                    <div className="text-2xl font-bold text-indigo-600">{totalPoints}</div>
                                    <div className="text-xs text-gray-400">total points</div>
                                </div>
                            </div>

                            {/* Right: Question editor */}
                            <div className="flex-1 overflow-y-auto p-5 space-y-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-semibold text-gray-700">Questions</p>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setAddQuestionOpen((v) => !v)}
                                            className="btn-primary w-auto mt-0 mr-0 ml-auto borderonly-btn text-xs px-3 py-1.5">
                                            <i className="fa-solid fa-plus mr-1 text-xs" />
                                            Add Question
                                        </button>
                                        {addQuestionOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setAddQuestionOpen(false)} />
                                                <ul className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg w-44 py-1">
                                                    <li
                                                        className="cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                        onClick={() => { handleAddQuestion(); setAddQuestionOpen(false); }}>
                                                        Multiple Choice
                                                    </li>
                                                    <li
                                                        className="cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                        onClick={() => { handleAddOpenText(); setAddQuestionOpen(false); }}>
                                                        Open Text
                                                    </li>
                                                </ul>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {questionArray.map((item, index) => (
                                    <div key={item.qId} id={`cq-Question${item.qId}`} className="border border-gray-200 rounded-xl p-4 bg-white">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">
                                                    {index + 1}
                                                </span>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {item.type === 'qt1' ? 'Multiple Choice' : 'Open Text'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!item.description && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDescriptionClick(item.qId)}
                                                        className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded border border-gray-200 hover:border-gray-300 transition-colors">
                                                        + Description
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleCloseQuestion(item.qId)}
                                                    className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1 rounded border border-gray-200 hover:border-red-200">
                                                    <i className="fa-solid fa-trash text-xs" />
                                                </button>
                                            </div>
                                        </div>

                                        <input
                                            type="text"
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg mb-2 focus:outline-none focus:border-indigo-400 bg-gray-50"
                                            placeholder="What would you like to ask?"
                                            {...register(`question_${item.qId}`)}
                                        />

                                        {item.description && (
                                            <input
                                                type="text"
                                                className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg mb-2 focus:outline-none focus:border-indigo-400 bg-gray-50"
                                                placeholder="Description (visible to participants)"
                                                {...register(`description_${item.qId}`)}
                                            />
                                        )}

                                        {item.type === 'qt1' && (
                                            <div className="space-y-2 mt-3">
                                                <p className="text-xs text-gray-400 font-medium">
                                                    Answer choices — select correct answer
                                                </p>
                                                {item.option.map((option, optIdx) => (
                                                    <div key={optIdx} className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name={`is_Answer_${item.qId}`}
                                                            className="form-check-input radio-btn flex-shrink-0"
                                                            checked={option.isAnswer}
                                                            onChange={() => handleIsAnswerClick(item.qId, optIdx)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className={`flex-1 text-sm px-3 py-2 border rounded-lg focus:outline-none focus:border-indigo-400 ${option.isAnswer ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}
                                                            placeholder={`Option ${optIdx + 1}`}
                                                            {...register(`${option.option}_${optIdx}`)}
                                                        />
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddOption(item.qId)}
                                                    className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 mt-1 px-2 py-1 rounded border border-dashed border-indigo-200 hover:border-indigo-400 transition-colors w-full justify-center">
                                                    <i className="fa-solid fa-plus text-xs" />
                                                    Add option
                                                </button>
                                            </div>
                                        )}

                                        {item.type === 'qt2' && (
                                            <div className="space-y-3 mt-3">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <div className="form-check form-switch pl-0 relative">
                                                        <input
                                                            className="form-check-input switch-btn"
                                                            type="checkbox"
                                                            role="switch"
                                                            defaultChecked={item.allowResourceUpload}
                                                            {...register(`allowResourceUpload_${item.qId}`)}
                                                        />
                                                        <i className="fas fa-check checkbox-tick hidden" />
                                                        <i className="fas fa-times checkbox-cross" />
                                                    </div>
                                                    <span className="text-sm text-gray-600">Allow resource upload</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <div className="form-check form-switch pl-0 relative">
                                                        <input
                                                            className="form-check-input switch-btn"
                                                            type="checkbox"
                                                            role="switch"
                                                            defaultChecked={item.isTextAnswer}
                                                            {...register(`isTextAnswer_${item.qId}`, {
                                                                onChange: () => handleIsAnswerTextClick(item.qId)
                                                            })}
                                                        />
                                                        <i className="fas fa-check checkbox-tick hidden" />
                                                        <i className="fas fa-times checkbox-cross" />
                                                    </div>
                                                    <span className="text-sm text-gray-600">Has a text answer</span>
                                                </label>
                                                {item.isTextAnswer && (
                                                    <input
                                                        type="text"
                                                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-gray-50"
                                                        placeholder="Enter the correct answer"
                                                        {...register(`text_answer_${item.qId}`)}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                                            <span className="text-xs font-medium text-gray-500">Points</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="w-20 text-sm px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-indigo-400"
                                                defaultValue={1}
                                                {...register(`points_${item.qId}`)}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {questionArray.length === 0 && (
                                    <div className="text-center py-10 text-gray-400 text-sm">
                                        {`No questions yet. Click "Add Question" to start.`}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                            <div>
                                {answerSelectError && isDataSubmitted && (
                                    <p className="text-red-500 text-xs">
                                        <i className="fa-solid fa-triangle-exclamation mr-1" />
                                        Please select a correct answer for each multiple choice question
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={isPollSavedLoading}
                                    onClick={() => handleSubmit(onSubmit)()}
                                    className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${isPollSavedLoading ? 'bg-indigo-300 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                                    {isPollSavedLoading ? (
                                        <>Saving… <Loader /></>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-floppy-disk text-xs" />
                                            {isEditMode ? 'Update Quiz' : 'Save Quiz'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Success  */}
                {step === 'success' && (
                    <div className="flex flex-col items-center justify-center py-12 px-8 gap-4">
                        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                            <i className="fa-solid fa-circle-check text-green-500 text-2xl" />
                        </div>
                        <div className="text-center">
                            <p className="text-base font-semibold text-gray-800 mb-1">
                                {isEditMode ? 'Quiz updated!' : 'Quiz saved!'}
                            </p>
                            <p className="text-sm text-gray-500">
                                The quiz has been {isEditMode ? 'updated for' : 'attached to'}{' '}
                                <strong className="text-gray-700">{selectedVideo?.name}</strong>.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-2">
                            <button
                                type="button"
                                onClick={handleAddAnother}
                                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors">
                                {isEditMode ? 'Back to quiz list' : 'Add another quiz'}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                                Done — back to course
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default CourseQuizBuilderModal;